import { Navigate, Outlet, useLocation } from 'react-router-dom';
import useSWR from 'swr';

interface User {
  id: string;
  email: string;
  name?: string;
  emailVerified?: boolean;
  // Billing (now on user, not team)
  stripeSubscriptionId?: string | null;
  isFreeAccount?: boolean;
  workspaceId?: number | null;
  workspaceSetupComplete?: boolean;
}

export default function ProtectedRoute() {
  const location = useLocation();
  const { data: user, error: userError, isLoading: userLoading } = useSWR<User>('/api/user');

  // Show loading while checking auth
  if (userLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-10 w-10 border-2 border-info-solid border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Redirect to sign-in if not authenticated
  if (userError || !user) {
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }

  // Check if user needs to verify email
  const isOnVerifyEmail = location.pathname === '/verify-email';
  if (user && user.emailVerified === false && !isOnVerifyEmail) {
    return <Navigate to="/verify-email" replace />;
  }

  // Check if user needs to complete checkout (no subscription)
  const isOnSelectPlan = location.pathname === '/onboarding/select-plan';
  const needsCheckout = !user.stripeSubscriptionId && !user.isFreeAccount;

  if (needsCheckout && !isOnSelectPlan) {
    return <Navigate to="/onboarding/select-plan" replace />;
  }

  // If on select-plan but already has subscription, redirect to dashboard
  if (isOnSelectPlan && user.stripeSubscriptionId) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
}
